import { PractitionerType, type TaxonomyOption } from "cmw-client";
import type { NestedOptionGroup } from "@/components/input";
import { PRACTITIONER_TYPE_LABELS } from "@/components/practitioner-card";

type FilterOption = { value: string; label: string };

export const GENDER_OPTIONS: FilterOption[] = [
  { value: "female", label: "Female" },
  { value: "male", label: "Male" },
];

export const PRACTITIONER_TYPE_OPTIONS: FilterOption[] = Object.values(PractitionerType).map((type) => ({
  value: type,
  label: PRACTITIONER_TYPE_LABELS[type],
}));

export const SESSION_FORMAT_LABELS: Record<string, string> = {
  "in-person": "In-Person",
  telehealth: "Telehealth",
  "home-visit": "Home Visit",
};

export const toFilterOptions = (options?: TaxonomyOption[]): FilterOption[] =>
  (options ?? []).flatMap((option) => [
    { value: option.slug, label: option.label },
    ...(option.children ?? []).map((child) => ({
      value: child.slug,
      label: `${option.label} › ${child.label}`,
    })),
  ]);

/** Every slug in a taxonomy — parents and children alike — against its own bare label. */
export const toLabelMap = (options?: TaxonomyOption[]): Record<string, string> =>
  Object.fromEntries(
    (options ?? []).flatMap<[string, string]>((option) => [
      [option.slug, option.label],
      ...(option.children ?? []).map((child): [string, string] => [child.slug, child.label]),
    ]),
  );

/**
 * The same list, nested one level for the grouped combobox. A category with children becomes a
 * named group; anything without children falls into an unheaded group at the end.
 */
export const toFilterGroups = (options?: TaxonomyOption[]): NestedOptionGroup[] => {
  const groups: NestedOptionGroup[] = [];
  const ungrouped: NestedOptionGroup = { label: "", options: [] };

  for (const option of options ?? []) {
    if (option.children && option.children.length > 0) {
      groups.push({
        label: option.label,
        options: option.children.map((child) => ({ value: child.slug, label: child.label })),
      });
    } else {
      ungrouped.options.push({ value: option.slug, label: option.label });
    }
  }

  return ungrouped.options.length > 0 ? [...groups, ungrouped] : groups;
};
